import { PortableTextComponents } from "@portabletext/react";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "@sanity/types";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link as MUILink,
} from "@mui/material";

type PortableTextValue = PortableTextBlock[];

interface ImageValue {
  asset?: {
    url?: string;
  };
  caption?: string;
}

interface TableRowData {
  _key?: string;
  cells?: TableCellData[];
}

interface TableCellData {
  content?: PortableTextValue;
  text?: string;
}

interface TableValue {
  title?: string;
  caption?: string;
  rows: TableRowData[];
}

interface CalloutValue {
  type?: "info" | "warning" | "error" | "success";
  title?: string;
  content?: string;
}

interface LinkValue {
  href?: string;
}

interface RenderProps {
  children?: React.ReactNode;
}

interface ImageProps extends RenderProps {
  value?: ImageValue;
}

interface TableProps extends RenderProps {
  value?: TableValue;
}

interface CalloutProps extends RenderProps {
  value?: CalloutValue;
}

interface LinkProps extends RenderProps {
  value?: LinkValue;
}

interface BlockProps extends RenderProps {
  children?: React.ReactNode;
}

interface ListProps extends RenderProps {
  children?: React.ReactNode;
}

interface ListItemProps extends RenderProps {
  children?: React.ReactNode;
}

const components: PortableTextComponents = {
  types: {
    image: ({ value }: ImageProps) => {
      if (!value?.asset?.url) return null;

      return (
        <Box component="figure" sx={{ my: 4 }}>
          <Box
            component="img"
            src={value.asset.url}
            alt="Blog post image"
            sx={{
              width: "100%",
              height: "auto",
              borderRadius: 2,
              display: "block",
              mx: "auto",
            }}
          />
          {value.caption ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                fontStyle: "italic",
                color: "text.secondary",
              }}
            >
              {value.caption}
            </Typography>
          ) : null}
        </Box>
      );
    },

    table: ({ value }: TableProps) => {
      if (!value?.rows?.length) return null;

      const renderCellContent = (
        cell: TableCellData | string
      ): React.ReactNode => {
        if (!cell) return null;

        if (typeof cell === "string") {
          if (cell.startsWith("http://") || cell.startsWith("https://")) {
            return (
              <MUILink href={cell} target="_blank" rel="noopener noreferrer">
                {cell}
              </MUILink>
            );
          }
          return cell;
        }

        if (cell.content) {
          return <PortableText value={cell.content} components={components} />;
        }

        return cell.text ?? null;
      };

      return (
        <Box component="figure" sx={{ my: 4 }}>
          {value.title && (
            <Typography variant="h6" sx={{ mb: 2 }}>
              {value.title}
            </Typography>
          )}
          <Paper
            sx={{
              width: "100%",
              overflowX: "auto",
              bgcolor: "background.paper",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {value.rows[0].cells?.map(
                    (cell: TableCellData | string, idx: number) => (
                      <TableCell key={idx} sx={{ fontWeight: 600 }}>
                        {renderCellContent(cell)}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {value.rows
                  .slice(1)
                  .map((row: TableRowData, rowIndex: number) => (
                    <TableRow key={row._key || rowIndex}>
                      {row.cells?.map(
                        (cell: TableCellData | string, cellIndex: number) => (
                          <TableCell key={cellIndex}>
                            {renderCellContent(cell)}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Paper>
          {value.caption ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1,
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              {value.caption}
            </Typography>
          ) : null}
        </Box>
      );
    },

    callout: ({ value }: CalloutProps) => {
      const tones: Record<string, string> = {
        info: "#e3f2fd",
        warning: "#fff3e0",
        error: "#ffebee",
        success: "#e8f5e9",
      };

      const bg = value?.type ? tones[value.type] : tones.info;

      return (
        <Box
          sx={{
            my: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: bg,
          }}
        >
          {value?.title ? (
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {value?.title}
            </Typography>
          ) : null}
          {value?.content ? (
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {value.content}
            </Typography>
          ) : null}
        </Box>
      );
    },
  },

  marks: {
    link: ({ children, value }: LinkProps) => {
      const isExternal = value?.href?.startsWith("http");
      return (
        <MUILink
          href={value?.href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {children}
        </MUILink>
      );
    },
  },

  block: {
    h1: ({ children }: BlockProps) => (
      <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    h2: ({ children }: BlockProps) => (
      <Typography variant="h4" component="h2" sx={{ mb: 2.5, fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }: BlockProps) => (
      <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    h4: ({ children }: BlockProps) => (
      <Typography variant="h6" component="h4" sx={{ mb: 1.5, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    blockquote: ({ children }: BlockProps) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: 4,
          borderColor: "primary.main",
          pl: 2,
          my: 3,
        }}
      >
        <Typography variant="body1" sx={{ fontStyle: "italic" }}>
          {children}
        </Typography>
      </Box>
    ),
    normal: ({ children }: BlockProps) => (
      <Typography
        variant="body1"
        sx={{ mb: 2, lineHeight: 1.7, fontSize: { md: "16px", xs: "14px" } }}
      >
        {children}
      </Typography>
    ),
  },

  list: {
    bullet: ({ children }: ListProps) => (
      <Box component="ul" sx={{ pl: 3, mb: 2 }}>
        {children}
      </Box>
    ),
    number: ({ children }: ListProps) => (
      <Box component="ol" sx={{ pl: 3, mb: 2 }}>
        {children}
      </Box>
    ),
  },

  listItem: {
    bullet: ({ children }: ListItemProps) => (
      <Box component="li" sx={{ mb: 1 }}>
        <Typography variant="body1">{children}</Typography>
      </Box>
    ),
    number: ({ children }: ListItemProps) => (
      <Box component="li" sx={{ mb: 1 }}>
        <Typography variant="body1">{children}</Typography>
      </Box>
    ),
  },
};

export default function BlogPortableText({
  value,
}: {
  value: PortableTextValue;
}) {
  if (!value) return null;
  return <PortableText value={value} components={components} />;
}
